import { Database } from "@/types/supabase"

// Database table types
export type Tables = Database["public"]["Tables"]
export type SleepSession = Tables["sleep_sessions"]["Row"]
export type SleepSessionInsert = Tables["sleep_sessions"]["Insert"]
export type SleepSessionUpdate = Tables["sleep_sessions"]["Update"]
export type NapSession = Tables["nap_sessions"]["Row"]
export type NapSessionInsert = Tables["nap_sessions"]["Insert"]
export type NapSessionUpdate = Tables["nap_sessions"]["Update"]
export type FeedingSessionRow = Tables["feeding_sessions"]["Row"]
export type FeedingSessionInsert = Tables["feeding_sessions"]["Insert"]
export type FeedingSessionUpdate = Tables["feeding_sessions"]["Update"]

// Enhanced FeedingSession type to handle nested structure from API
export type FeedingSession = FeedingSessionRow | {
  session: FeedingSessionRow;
  id: string;
}

// Lookup tables types
export interface LookupItem {
  id: string
  name?: string
  description?: string
  level?: string
}

export interface LookupTables {
  locations: LookupItem[]
  environments: LookupItem[]
  onsetMethods: LookupItem[]
  restfulnessRatings: LookupItem[]
  sleepSounds: LookupItem[]
  soundLevels: LookupItem[]
}

// Utility functions for working with activities
export function getTableNameByActivityType(type: 'sleep' | 'nap' | 'feeding'): string {
  switch (type) {
    case 'sleep':
      return 'sleep_sessions'
    case 'nap':
      return 'nap_sessions'
    case 'feeding':
      return 'feeding_sessions'
    default:
      return ''
  }
}

// Extended types for joined data
export interface ReferenceItem {
  id: string;
  name?: string;
  description?: string;
  rating?: string;
  method?: string;
  level?: string;
}

export interface ActivityType {
  id: string
  type: 'sleep' | 'nap' | 'feeding'
  startTime: string
  endTime: string
  duration: number
  quality?: string | null
  location?: string | null
  environment?: string | null
  onset_method?: string | null
  sound?: string | null
  sound_level?: string | null
  notes: string
  signs_of_sleep_debt?: boolean | null 
  childId: string
  caregiver_id?: string
  
  // Display names derived from joined tables
  qualityName?: string | null
  locationName?: string | null
  environmentName?: string | null
  onsetMethodName?: string | null
  soundName?: string | null
  soundLevelName?: string | null
  
  // Feeding specific
  feeding_type?: string
  feeding_method?: string
  feeding_side?: string
  feeding_amount?: number
  feeding_unit?: string
  temperature?: "room" | "body" | "warm" | string | null
  brand?: string | null 
  
  // Sleep specific
  sleep_latency?: number
  
  // Added fields for breast feeding
  breast?: string | null   
  left_duration?: number | null  
  right_duration?: number | null 
}

export interface SleepSessionWithJoins extends SleepSession {
  restfulness: ReferenceItem | null;
  location: ReferenceItem | null;
  environment: ReferenceItem | null;
  onset_methods: { id: string, method: string } | null;
  sound: ReferenceItem | null;
  sound_level: ReferenceItem | null;
}

/**
 * Extended type for nap sessions with joined reference tables
 */
export interface NapSessionWithJoins extends NapSession {
  // Joined fields from reference tables
  sleep_onset_method: { 
    id: string;
  } | null;
  
  restfulness_rating: { 
    id: string;
  } | null;
  
  location: { 
    id: string;
    description: string 
  } | null;
  
  sleep_environment: { 
    id: string;
    description: string 
  } | null;
}

export interface FeedingSessionWithJoins extends FeedingSessionRow {
  // Join with child table
  child: { id: string; name: string } | null;
  
  // Specialized feeding session types based on joins
  breastfeeding?: {
    id: string;
    left_duration: number;
    right_duration: number;
    feeding_order: string[];
    session_id: string;
  }[];
  
  bottle_sessions?: {
    id: string;
    amount_ml: number;
    amount_oz: number | null;
    feeding_duration?: number | null;
    session_id: string;
  }[];
  
  formula_sessions?: {
    id: string;
    amount_ml: number;
    amount_oz: number | null;
    brand: string;
    prepared_by?: string | null;
    session_id: string;
  }[];
}

// Fetch lookup data with proper typing
export async function fetchLookupData(): Promise<LookupTables> {
  const { supabase } = await import('@/integrations/supabase/client')
  
  // Fetch all lookup data in parallel
  const [
    { data: locations },
    { data: environments },
    { data: onsetMethods },
    { data: restfulnessRatings },
    { data: sleepSounds },
    { data: soundLevels }
  ] = await Promise.all([
    supabase.from('nap_location').select('*'),
    supabase.from('sleep_environment').select('*'),
    supabase.from('sleep_onset_method').select('*'),
    supabase.from('restfulness_rating').select('*'),
    supabase.from('sleep_sounds').select('*'),
    supabase.from('sound_levels').select('*')
  ])
  
  return {
    locations: locations || [],
    environments: environments || [],
    onsetMethods: onsetMethods || [],
    restfulnessRatings: restfulnessRatings || [],
    sleepSounds: sleepSounds || [],
    soundLevels: soundLevels || []
  }
}

// Get lookup item name from id
export function getLookupItemName(items: LookupItem[], id: string | null | undefined): string {
  if (!id) return 'Unknown'
  
  const item = items.find(item => item.id === id)
  return item ? (item.name || item.description || item.level || 'Unknown') : 'Unknown'
}

// Define a strongly typed interface for feeding activity that combines database and UI properties
export interface FeedingActivity {
  // Core feeding session fields (required)
  id: string;
  type: Database["public"]["Enums"]["feeding_type_enum"];
  
  // Core feeding session fields (optional)
  childId?: string;
  caregiver_id?: string;
  startTime?: string;
  endTime?: string;
  notes?: string | null;
  
  // UI-specific fields that don't map directly to the database
  duration?: number;           // Calculated from start/end time
  amount?: number;             // Maps to amount_ml in bottle/formula sessions
  side?: "left" | "right";     // Maps to feeding_order in breastfeeding_sessions
  brand?: string;              // Maps to brand in formula_sessions
  temperature?: "room" | "body" | "warm"; // Used for formula temperature
  
  // Breast feeding specific durations
  left_duration?: number;      // Duration in minutes for left breast
  right_duration?: number;     // Duration in minutes for right breast
}

// Interface for sleep windows chart data visualization
export interface SleepWindowsData {
  hour_label: string;
  sleep_start_count: number;
  sleep_end_count: number;
}

export interface SleepDurationData {
  duration: string;
  count: number;
}
